import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Code as CodeIcon,
  List as ListIcon,
  NoteAdd as NewIcon
} from '@mui/icons-material';
import api from './api';

const languageColors = {
  python: '#3572A5',
  csharp: '#178600',
  javascript: '#F7DF1E',
  sql: '#336791'
};

const CodeEditor = () => {
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [isLoading, setIsLoading] = useState(false);
  const [showIOConfig, setShowIOConfig] = useState(false);
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [currentCodeName, setCurrentCodeName] = useState('');
  const [outputContent, setOutputContent] = useState('');
  const [showCodeList, setShowCodeList] = useState(false);
  const [codeList, setCodeList] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newCodeName, setNewCodeName] = useState('');
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testInputValues, setTestInputValues] = useState({});
  const [outputDialogOpen, setOutputDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const outputTimerRef = useRef(null);

  useEffect(() => {
    // ResizeObserver hata yakalayıcı
    const originalHandler = window.onerror;
    function errorHandler(message, source, lineno, colno, error) {
      if (message.toString().includes('ResizeObserver loop') ||
          (error && error.message && error.message.includes('ResizeObserver loop'))) {
        return true;
      }
      if (typeof originalHandler === 'function') {
        return originalHandler(message, source, lineno, colno, error);
      }
      return false;
    }
    window.onerror = errorHandler;
    return () => { window.onerror = originalHandler; };
  }, []);

  useEffect(() => {
    loadCodeList();
  }, []);

  const loadCodeList = async () => {
    try {
      // Get companyId from multiple sources
      let companyId = null;

      // Try api.getContext first
      try {
        const context = await api.getContext();
        companyId = context?.companyId;
      } catch (e) {
        console.log('api.getContext failed:', e);
      }

      // Fallback to sessionStorage
      if (!companyId) {
        const sessionCompanyId = sessionStorage.getItem('TEMPCOM');
        if (sessionCompanyId) {
          try {
            companyId = parseInt(JSON.parse(sessionCompanyId));
          } catch (e) {
            companyId = parseInt(sessionCompanyId);
          }
        }
      }

      // Default to 3 if still no companyId
      if (!companyId) {
        companyId = 3;
      }

      console.log('Loading codes for companyId:', companyId);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://app-rmmsapi.azurewebsites.net/api'}/Flow/codes/${companyId}`);
      console.log('API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Codes loaded:', data?.length || 0);
        setCodeList(data || []);
      } else {
        console.log('API failed, using localStorage');
        const localScripts = JSON.parse(localStorage.getItem('codeScripts') || '[]');
        setCodeList(localScripts);
      }
    } catch (error) {
      console.error('Error loading code list:', error);
      const localScripts = JSON.parse(localStorage.getItem('codeScripts') || '[]');
      setCodeList(localScripts);
    }
  };

  const handleNew = () => {
    setCode('');
    setLanguage('python');
    setCurrentCodeName('');
    setInputs([]);
    setOutputs([]);
    setOutputContent('');
  };

  const handleSave = async () => {
    if (!code.trim()) {
      setSnackbar({ open: true, message: 'Kod boş olamaz', severity: 'warning' });
      return;
    }

    if (!currentCodeName) {
      setSaveDialogOpen(true);
      return;
    }

    try {
      const saveData = {
        name: currentCodeName,
        code: code,
        language: language,
        inputs: JSON.stringify(inputs),
        outputs: JSON.stringify(outputs)
      };

      const existingScripts = JSON.parse(localStorage.getItem('codeScripts') || '[]');
      const existingIndex = existingScripts.findIndex(s => s.name === currentCodeName);

      if (existingIndex >= 0) {
        existingScripts[existingIndex] = { ...saveData, id: existingScripts[existingIndex].id, updatedAt: new Date().toISOString() };
      } else {
        existingScripts.push({ ...saveData, id: Date.now(), createdAt: new Date().toISOString() });
      }

      localStorage.setItem('codeScripts', JSON.stringify(existingScripts));
      setSnackbar({ open: true, message: 'Kod kaydedildi', severity: 'success' });
      loadCodeList();
    } catch (error) {
      setSnackbar({ open: true, message: `Hata: ${error.message}`, severity: 'error' });
    }
  };

  const handleTest = () => {
    if (!currentCodeName) {
      setSnackbar({ open: true, message: 'Lütfen önce kodu kaydedin', severity: 'warning' });
      return;
    }

    const initialValues = {};
    inputs?.forEach(input => {
      switch(input.type) {
        case 'str': initialValues[input.name] = ''; break;
        case 'int': initialValues[input.name] = 0; break;
        case 'float': initialValues[input.name] = 0.0; break;
        case 'bool': initialValues[input.name] = false; break;
        case 'list': initialValues[input.name] = '[]'; break;
        case 'dict': initialValues[input.name] = '{}'; break;
        default: initialValues[input.name] = '';
      }
    });
    setTestInputValues(initialValues);
    setShowTestDialog(true);
  };

  const handleTestExecution = async () => {
    if (outputTimerRef.current) {
      clearTimeout(outputTimerRef.current);
    }

    setIsLoading(true);
    setShowTestDialog(false);

    try {
      // Process input values
      const processedInputs = {};
      inputs?.forEach(input => {
        const value = testInputValues[input.name];
        if (input.type === 'list' || input.type === 'dict') {
          try {
            processedInputs[input.name] = JSON.parse(value || (input.type === 'list' ? '[]' : '{}'));
          } catch (e) {
            processedInputs[input.name] = input.type === 'list' ? [] : {};
          }
        } else if (input.type === 'int') {
          processedInputs[input.name] = parseInt(value || '0', 10);
        } else if (input.type === 'float') {
          processedInputs[input.name] = parseFloat(value || '0');
        } else if (input.type === 'bool') {
          processedInputs[input.name] = value === 'true' || value === true;
        } else {
          processedInputs[input.name] = value || '';
        }
      });

      // Get companyId
      let companyId = 3;
      try {
        const sessionCompanyId = sessionStorage.getItem('TEMPCOM');
        if (sessionCompanyId) {
          companyId = parseInt(JSON.parse(sessionCompanyId)) || 3;
        }
      } catch (e) {}

      // Determine execution endpoint based on language
      const engineUrl = 'https://rmms-metis-engine.azurewebsites.net/api/CodeExecution';
      let endpoint = '';
      if (language === 'python') {
        endpoint = `${engineUrl}/python`;
      } else if (language === 'csharp') {
        endpoint = `${engineUrl}/csharp`;
      } else {
        // For unsupported languages, show simulation
        let finalOutput = '=== Çalıştırma Sonuçları ===\n';
        finalOutput += `\nDil: ${language}`;
        finalOutput += `\nSatır: ${code.split('\n').length}`;
        finalOutput += `\nKarakter: ${code.length}`;
        finalOutput += `\n\nGirdiler: ${JSON.stringify(processedInputs, null, 2)}`;
        finalOutput += `\n\n[Simülasyon] ${language} dili henüz desteklenmiyor.`;
        setOutputContent(finalOutput);
        setSnackbar({ open: true, message: `${language} dili henüz desteklenmiyor`, severity: 'warning' });
        setOutputDialogOpen(true);
        setIsLoading(false);
        return;
      }

      // Execute code via MetisEngine API
      console.log('Executing code via:', endpoint);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Code: code,
          Parameters: processedInputs,
          companyId: companyId
        })
      });

      let finalOutput = '=== Çalıştırma Sonuçları ===\n';
      finalOutput += `\nDil: ${language}`;
      finalOutput += `\nSatır: ${code.split('\n').length}`;
      finalOutput += `\nKarakter: ${code.length}`;
      finalOutput += `\n\nGirdiler: ${JSON.stringify(processedInputs, null, 2)}`;

      if (response.ok) {
        const result = await response.json();
        finalOutput += '\n\n=== Çıktı ===\n';
        if (result.Result !== undefined && result.Result !== null) {
          if (typeof result.Result === 'object') {
            finalOutput += JSON.stringify(result.Result, null, 2);
          } else {
            finalOutput += String(result.Result);
          }
        } else if (result.result !== undefined && result.result !== null) {
          if (typeof result.result === 'object') {
            finalOutput += JSON.stringify(result.result, null, 2);
          } else {
            finalOutput += String(result.result);
          }
        } else {
          finalOutput += JSON.stringify(result, null, 2);
        }
        setSnackbar({ open: true, message: 'Kod başarıyla çalıştırıldı', severity: 'success' });
      } else {
        const errorData = await response.json().catch(() => ({ Error: 'Bilinmeyen hata' }));
        finalOutput += '\n\n=== Hata ===\n';
        finalOutput += errorData.Error || errorData.error || JSON.stringify(errorData);
        setSnackbar({ open: true, message: 'Kod çalıştırma hatası', severity: 'error' });
      }

      setOutputContent(finalOutput);
      setOutputDialogOpen(true);
    } catch (error) {
      console.error('Execution error:', error);
      setOutputContent('Hata: ' + error.message);
      setSnackbar({ open: true, message: `Hata: ${error.message}`, severity: 'error' });
      setOutputDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSelect = (selectedCode) => {
    try {
      let parsedInputs = [];
      let parsedOutputs = [];

      if (selectedCode.inputs) {
        parsedInputs = typeof selectedCode.inputs === 'string'
          ? JSON.parse(selectedCode.inputs) : selectedCode.inputs || [];
      }
      if (selectedCode.outputs) {
        parsedOutputs = typeof selectedCode.outputs === 'string'
          ? JSON.parse(selectedCode.outputs) : selectedCode.outputs || [];
      }

      setCode(selectedCode.code || '');
      setLanguage(selectedCode.language || 'python');
      setInputs(parsedInputs);
      setOutputs(parsedOutputs);
      setCurrentCodeName(selectedCode.name);
      setShowCodeList(false);
      setSnackbar({ open: true, message: `"${selectedCode.name}" yüklendi`, severity: 'info' });
    } catch (error) {
      setSnackbar({ open: true, message: `Yükleme hatası: ${error.message}`, severity: 'error' });
    }
  };

  const handleSaveWithName = () => {
    if (!newCodeName.trim()) {
      setSnackbar({ open: true, message: 'Kod adı gerekli', severity: 'warning' });
      return;
    }

    try {
      const saveData = {
        id: Date.now(),
        name: newCodeName,
        code: code,
        language: language,
        inputs: JSON.stringify(inputs),
        outputs: JSON.stringify(outputs),
        createdAt: new Date().toISOString()
      };

      const existingScripts = JSON.parse(localStorage.getItem('codeScripts') || '[]');
      existingScripts.push(saveData);
      localStorage.setItem('codeScripts', JSON.stringify(existingScripts));

      setCurrentCodeName(newCodeName);
      setNewCodeName('');
      setSaveDialogOpen(false);
      setSnackbar({ open: true, message: 'Kod kaydedildi', severity: 'success' });
      loadCodeList();
    } catch (error) {
      setSnackbar({ open: true, message: `Hata: ${error.message}`, severity: 'error' });
    }
  };

  const handleDeleteCode = (codeItem, e) => {
    e.stopPropagation();
    const existingScripts = JSON.parse(localStorage.getItem('codeScripts') || '[]');
    const filtered = existingScripts.filter(s => s.name !== codeItem.name);
    localStorage.setItem('codeScripts', JSON.stringify(filtered));

    if (currentCodeName === codeItem.name) {
      handleNew();
    }
    loadCodeList();
    setSnackbar({ open: true, message: 'Kod silindi', severity: 'success' });
  };

  return (
    <Box p={3} sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon fontSize="large" />
            Kod Editörü
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Script oluşturun, düzenleyin ve test edin
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Dil</InputLabel>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              label="Dil"
            >
              <MenuItem value="python">Python</MenuItem>
              <MenuItem value="csharp">C#</MenuItem>
              <MenuItem value="javascript">JavaScript</MenuItem>
              <MenuItem value="sql">SQL</MenuItem>
            </Select>
          </FormControl>

          {currentCodeName && (
            <Chip
              label={currentCodeName}
              color="primary"
              variant="outlined"
              size="small"
              icon={<CodeIcon />}
            />
          )}

          <Divider orientation="vertical" flexItem />

          <Button variant="outlined" size="small" startIcon={<NewIcon />} onClick={handleNew}>
            Yeni
          </Button>
          <Button variant="outlined" size="small" startIcon={<ListIcon />} onClick={() => setShowCodeList(true)}>
            Kodlar ({codeList.length})
          </Button>
          <Tooltip title="I/O Ayarları">
            <IconButton onClick={() => setShowIOConfig(true)} size="small">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            color="success"
            startIcon={isLoading ? <CircularProgress size={16} /> : <PlayIcon />}
            onClick={handleTest}
            disabled={isLoading}
          >
            Test
          </Button>
          <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSave}>
            Kaydet
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper sx={{ p: 0, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Editor */}
        <Box sx={{ flex: 1, minHeight: 400 }}>
          <Editor
            height="100%"
            language={language === 'csharp' ? 'csharp' : language.toLowerCase()}
            value={code}
            onChange={setCode}
            theme="vs-dark"
            options={{
              minimap: { enabled: true },
              fontSize: 14,
              lineNumbers: 'on',
              automaticLayout: true,
              scrollBeyondLastLine: false,
              tabSize: 4,
              insertSpaces: true
            }}
          />
        </Box>
      </Paper>

      {/* Code List Dialog */}
      <Dialog open={showCodeList} onClose={() => setShowCodeList(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Kayıtlı Kodlar
          <IconButton onClick={() => setShowCodeList(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {codeList.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
              Henüz kayıtlı kod yok
            </Typography>
          ) : (
            <List>
              {codeList.map((item, index) => (
                <ListItem
                  key={item.name || index}
                  button
                  onClick={() => handleCodeSelect(item)}
                  sx={{
                    borderLeft: `4px solid ${languageColors[item.language?.toLowerCase()] || '#666'}`,
                    mb: 1,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                >
                  <ListItemText
                    primary={item.name}
                    secondary={item.language?.toUpperCase()}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={(e) => handleDeleteCode(item, e)} size="small">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Kodu Kaydet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Kod Adı"
            value={newCodeName}
            onChange={(e) => setNewCodeName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleSaveWithName}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* I/O Config Dialog */}
      <Dialog open={showIOConfig} onClose={() => setShowIOConfig(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          I/O Yapılandırması
          <IconButton onClick={() => setShowIOConfig(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Girdiler (Inputs)</Typography>
            {inputs?.map((input, index) => (
              <Box key={index} sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Ad"
                  value={input.name}
                  onChange={(e) => {
                    const newInputs = [...inputs];
                    newInputs[index].name = e.target.value;
                    setInputs(newInputs);
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={input.type}
                    onChange={(e) => {
                      const newInputs = [...inputs];
                      newInputs[index].type = e.target.value;
                      setInputs(newInputs);
                    }}
                  >
                    {['str', 'int', 'float', 'bool', 'list', 'dict'].map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton color="error" onClick={() => setInputs(inputs.filter((_, i) => i !== index))}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setInputs([...inputs, { name: '', type: 'str' }])}>
              Girdi Ekle
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="h6" gutterBottom>Çıktılar (Outputs)</Typography>
            {outputs?.map((output, index) => (
              <Box key={index} sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Ad"
                  value={output.name}
                  onChange={(e) => {
                    const newOutputs = [...outputs];
                    newOutputs[index].name = e.target.value;
                    setOutputs(newOutputs);
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={output.type}
                    onChange={(e) => {
                      const newOutputs = [...outputs];
                      newOutputs[index].type = e.target.value;
                      setOutputs(newOutputs);
                    }}
                  >
                    {['str', 'int', 'float', 'bool', 'list', 'dict'].map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton color="error" onClick={() => setOutputs(outputs.filter((_, i) => i !== index))}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setOutputs([...outputs, { name: '', type: 'str' }])}>
              Çıktı Ekle
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIOConfig(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={showTestDialog} onClose={() => setShowTestDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kod Testi</DialogTitle>
        <DialogContent dividers>
          {inputs?.length > 0 ? (
            inputs.map((input, index) => (
              <TextField
                key={index}
                fullWidth
                label={`${input.name} (${input.type})`}
                value={testInputValues[input.name] || ''}
                onChange={(e) => setTestInputValues({ ...testInputValues, [input.name]: e.target.value })}
                sx={{ mb: 2 }}
                multiline={input.type === 'list' || input.type === 'dict'}
                rows={input.type === 'list' || input.type === 'dict' ? 3 : 1}
              />
            ))
          ) : (
            <Typography color="textSecondary">
              Girdi tanımlanmadı. I/O Ayarları'ndan girdi ekleyebilirsiniz.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestDialog(false)}>İptal</Button>
          <Button variant="contained" onClick={handleTestExecution}>Çalıştır</Button>
        </DialogActions>
      </Dialog>

      {/* Output Dialog */}
      <Dialog open={outputDialogOpen} onClose={() => setOutputDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Çıktı Sonuçları
          <IconButton onClick={() => setOutputDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Paper sx={{
            p: 2,
            backgroundColor: '#1e1e1e',
            color: '#4ade80',
            fontFamily: 'monospace',
            fontSize: 14,
            whiteSpace: 'pre-wrap',
            minHeight: 200,
            maxHeight: 400,
            overflow: 'auto',
            border: '1px solid #333'
          }}>
            {outputContent || 'Çıktı bekleniyor...'}
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CodeEditor;
